import {
  ConfigApi,
  createApiRef,
  DiscoveryApi,
  IdentityApi,
} from '@backstage/core-plugin-api';
import { ApiUtil } from '@dewise-devex/backstage-plugin-utilities-common-client';
import { Entity } from '@backstage/catalog-model';
import { ProductDeployment } from '@dewise-devex/backstage-plugin-product-common';
import fetch from 'node-fetch';

export interface ICuxService {
  getDeploymentsStatus: (entity: Entity) => Promise<ProductDeployment[]>;
}

export const cuxApiRef = createApiRef<ICuxService>({
  id: 'plugin.customer-experience.client',
});
/**
 * `CuXService` class provides methods to interact with backend services
 * using authorized requests. It enables retrieving badge information,
 * achievements, and product deployment details by communicating with a specified API.
 */
export class CuXService implements ICuxService {
  private readonly identityApi: IdentityApi;
  private readonly discoveryApi: DiscoveryApi;
  private readonly PRODUCT_API_PATH = 'product';

  /**
   * Initializes an instance of `ApiClient`.
   *
   * @param options - The configuration options.
   * @param options.identityApi - The `IdentityApi` used for user authentication.
   * @param options.configApi - The `ConfigApi` used to access configuration settings.
   */
  constructor(options: {
    identityApi: IdentityApi;
    configApi: ConfigApi;
    discoveryApi: DiscoveryApi;
  }) {
    this.identityApi = options.identityApi;
    this.discoveryApi = options.discoveryApi;
  }

  private async request<T>(
    url: fetch.RequestInfo,
    options?: fetch.RequestInit,
  ): Promise<T> {
    const authorizedRequest = await ApiUtil.authorizeRequest(this.identityApi);
    return await authorizedRequest.execute(url, options);
  }

  async getDeploymentsStatus(entity: Entity) {
    const productApiUrl = await this.discoveryApi.getBaseUrl(
      this.PRODUCT_API_PATH,
    );
    const endpoint = `${productApiUrl}/${entity.metadata.namespace}/${entity.metadata.name}/deployments`;

    return await this.request<ProductDeployment[]>(endpoint);
  }
}
